import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import Dexie from 'dexie';
import { firstValueFrom, Observable } from 'rxjs';
import {
    JsonLocalStorageNotificationMaterialOrderModel,
    LocalStorageNotificationMaterialOrderModel,
    LocalStorageNotificationMaterialOrderModelFromJson,
} from '../../models/low-level/local-storage-notification-material-order.model';
import { JsonMaterialModel, MaterialModel } from '../../models/low-level/material.model';
import { LZStringService } from '../l-z-string/l-z-string.service';
import { StorageMaterialDbEntry, StorageMaterialInterface } from './storage-material.interface';
import { EncryptionService } from '@evorait/pace-encryption';
import { filter } from 'rxjs/operators';
import { UserMasterService } from '../user-master/user-master.service';

const KEY_MATERIAL_ORDER_ID = 2;

@Injectable({
    providedIn: 'root',
})
export class StorageMaterialService extends Dexie implements StorageMaterialInterface {
    public static readonly DB_NAME = 'MaterialDatabase';
    public static readonly DB_TABLE = 'materials';
    public static readonly DB_KEY_PATH = 'id';

    private static readonly VERSION_ONE = 1;
    private static readonly VERSION_TWO = StorageMaterialService.VERSION_ONE + 1;

    public static readonly STATIC_ID = 1;

    private materials: Dexie.Table<StorageMaterialDbEntry, number>;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly encryption: EncryptionService,
        private readonly LZString: LZStringService,
        private readonly userMasterService: UserMasterService,
    ) {
        super(StorageMaterialService.DB_NAME);
        this.version(StorageMaterialService.VERSION_ONE).stores({
            [StorageMaterialService.DB_TABLE]: StorageMaterialService.DB_KEY_PATH,
        });

        // encrypt potentially existing data
        this.version(StorageMaterialService.VERSION_TWO).upgrade(async (trans) => {
            await firstValueFrom(this.encryption.selectInitialized.pipe(filter(Boolean)));
            return trans
                .table(StorageMaterialService.DB_TABLE)
                .toCollection()
                .modify(async (material: StorageMaterialDbEntry) => {
                    if (!this.userMasterService.isPaceOnlineUser()) {
                        try {
                            const data = this.LZString.decompressFromUint8Array(material.materialsblob);
                            material.materialsblob = this.encryption.encryptAndCompress(data);
                        } catch {
                            this.log.error(
                                `[MaterialDatabaseService](DB version upgrade): Failed to upgrade item ${material.id} to version ${StorageMaterialService.VERSION_TWO}`,
                            );
                        }
                    }
                });
        });
    }

    public saveMaterials(materials: MaterialModel[], timestamp: number = Date.now()): Observable<void> {
        this.log.debug(`[MaterialDatabaseService](saveMaterials): Saving ${materials.length} materials.`);

        const json = JSON.stringify(materials);
        const compressed = this.encryption.encryptAndCompress(json);

        return new Observable((subscriber) => {
            this.materials
                .put({
                    id: StorageMaterialService.STATIC_ID,
                    materialsblob: compressed,
                    timestamp,
                })
                .then(() => {
                    subscriber.next();
                    subscriber.complete();
                })
                .catch((error) => {
                    const errorMessage = `[MaterialDatabaseService](saveMaterials): Error on saving materials to database: ${error}`;
                    this.log.error(errorMessage);
                    subscriber.error(new Error(errorMessage));
                });
        });
    }

    public getMaterials(): Observable<{ material: JsonMaterialModel[]; timestamp: number }> {
        return new Observable((subscriber) => {
            this.materials
                .get({
                    id: StorageMaterialService.STATIC_ID,
                })
                .then((compressedMaterialsJson) => {
                    if (!compressedMaterialsJson) {
                        subscriber.next({ material: [], timestamp: 0 });
                    } else {
                        const data = this.encryption.decryptAndDecompress(compressedMaterialsJson.materialsblob);
                        subscriber.next({
                            material: <JsonMaterialModel[]>JSON.parse(data) || [],
                            timestamp: compressedMaterialsJson.timestamp,
                        });
                    }
                    subscriber.complete();
                })
                .catch((error) => {
                    const errorMessage = `[MaterialDatabaseService](getMaterials): Error on reading materials from database: ${error}. Returning empty array instead`;
                    this.log.warn(errorMessage);
                    subscriber.next({ material: [], timestamp: 0 });
                    subscriber.complete();
                });
        });
    }

    /**
     * Get all the notifications for which material has been requested
     *
     * @returns an observable with the material orders and the timestamp
     */
    public getAllMaterialCreateOrders(): Observable<{
        material: LocalStorageNotificationMaterialOrderModel[];
        timestamp: number;
    }> {
        return new Observable((subscriber) => {
            this.materials
                .get({
                    id: KEY_MATERIAL_ORDER_ID,
                })
                .then((compressedNotificationMaterialOrdersJson) => {
                    if (!compressedNotificationMaterialOrdersJson) {
                        subscriber.next({ material: [], timestamp: 0 });
                    } else {
                        const data = this.encryption.decryptAndDecompress(
                            compressedNotificationMaterialOrdersJson.materialsblob,
                        );

                        const materials = <JsonLocalStorageNotificationMaterialOrderModel[]>JSON.parse(data) || [];
                        subscriber.next({
                            material: materials.map(
                                (jsonMaterial) => new LocalStorageNotificationMaterialOrderModelFromJson(jsonMaterial),
                            ),
                            timestamp: compressedNotificationMaterialOrdersJson.timestamp,
                        });
                    }
                    subscriber.complete();
                })
                .catch((error) => {
                    const errorMessage = `[MaterialDatabaseService](getAllMaterialCreateOrders): Error on reading request materials from database: ${error}. Returning empty array instead`;
                    this.log.warn(errorMessage);
                    subscriber.next({ material: [], timestamp: 0 });
                    subscriber.complete();
                });
        });
    }

    /**
     * Add a material order process in the local storage
     *
     * @param notificationMaterialOrders the material orders to save
     * @param timestamp the timestamp of the save operation
     *
     * @returns an observable that completes when the save operation is done
     */
    public saveMaterialCreateOrders(
        notificationMaterialOrders: LocalStorageNotificationMaterialOrderModel[],
        timestamp: number = Date.now(),
    ): Observable<void> {
        this.log.debug(
            `[MaterialDatabaseService](saveMaterialCreateOrders): Saving ${notificationMaterialOrders.length} material create orders.`,
        );

        const json = JSON.stringify(notificationMaterialOrders);
        const compressed = this.encryption.encryptAndCompress(json);

        return new Observable((subscriber) => {
            this.materials
                .put({
                    id: KEY_MATERIAL_ORDER_ID,
                    materialsblob: compressed,
                    timestamp,
                })
                .then(() => {
                    subscriber.next();
                    subscriber.complete();
                })
                .catch((error) => {
                    const errorMessage = `[MaterialDatabaseService](saveMaterialCreateOrders): Error on saving material request to database: ${error}`;
                    this.log.error(errorMessage);
                    subscriber.error(new Error(errorMessage));
                });
        });
    }
}
